import { useEffect, useRef, useState, type RefObject } from 'react';

import {
  cornerAt,
  cursorFor,
  resizeRect,
  type CardRect,
  type Corner,
  type SizeLimits,
} from './cornerResize';

/** How long the pointer rests in a corner's zone before the corner goes live (FR4). */
export const LIVE_DELAY_MS = 250;

/** The page attribute that holds the resize cursor over everything while a corner is dragged. */
export const RESIZING_ATTRIBUTE = 'data-tl-corner-resizing';
const RESIZING_STYLE_ID = 'tl-corner-resizing-cursor';

/**
 * While a corner is dragged the pointer often leaves the card (fast, or past a limit), and the
 * picture's drawing area under it sets cursors of its own on its elements, which a cursor on
 * `body` would only fall back to. One page rule, keyed on an attribute on `<html>` that exists only
 * for the drag, holds the resize cursor over every element until it ends.
 */
const holdResizeCursor = (cursor: string): (() => void) => {
  if (!document.getElementById(RESIZING_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = RESIZING_STYLE_ID;
    style.textContent = `html[${RESIZING_ATTRIBUTE}], html[${RESIZING_ATTRIBUTE}] * { cursor: var(--tl-corner-resizing-cursor) !important; }`;
    document.head.appendChild(style);
  }
  const root = document.documentElement;
  root.style.setProperty('--tl-corner-resizing-cursor', cursor);
  root.setAttribute(RESIZING_ATTRIBUTE, '');
  return () => {
    root.removeAttribute(RESIZING_ATTRIBUTE);
    root.style.removeProperty('--tl-corner-resizing-cursor');
  };
};

export interface CornerResizeOptions {
  /** The movable card's slot: the corner plan is portalled into it, so its DOM events pass here. */
  slotRef: RefObject<HTMLElement | null>;
  /** The cards layer, whose top-left is (0, 0) for `rect`. */
  layer: HTMLElement | null;
  enabled: boolean;
  /** The card as drawn now, in layer pixels; null until measured. */
  rect: CardRect | null;
  limits: SizeLimits;
  layerBox: { width: number; height: number };
  inset: number;
  /** The new place and size, on release of a resize that changed something (FR16). */
  onCommit: (rect: CardRect) => void;
}

export interface CornerResize {
  /** The corner that is live (FR4), or null. Kept through a resize. */
  live: Corner | null;
  /** While a resize is under way: the card as it is being drawn, and the corner pinned. */
  preview: { rect: CardRect; start: CardRect; corner: Corner } | null;
}

/**
 * Resizing the corner plan from a corner (`observers-corner-plan-resize`, FR1–FR17).
 *
 * Native listeners on the slot, not React handlers: the corner plan arrives by portal from another
 * React tree, so React's synthetic events from it never reach this one, but its DOM events bubble
 * through the slot. The press listener is in the CAPTURE phase so a live corner takes the press
 * before the plan's canvas can start a pan (FR10) — and it does nothing at all otherwise, so a
 * press near a corner before the pause ends is still the plan's (FR5).
 */
export const useCornerResize = ({
  slotRef,
  layer,
  enabled,
  rect,
  limits,
  layerBox,
  inset,
  onCommit,
}: CornerResizeOptions): CornerResize => {
  const [live, setLive] = useState<Corner | null>(null);
  const [preview, setPreview] = useState<CornerResize['preview']>(null);

  // The listeners are attached once per slot; everything they read changes per render.
  const latest = useRef({ rect, limits, layerBox, inset, onCommit, live });
  latest.current = { rect, limits, layerBox, inset, onCommit, live };

  useEffect(() => {
    const slot = slotRef.current;
    if (!enabled || !slot || !layer) return;
    let timer: number | null = null;
    /** The zone the pointer is resting in, live or not. */
    let zone: Corner | null = null;
    /** The resize under way: its pointer, and how to end it without committing. */
    let drag: { pointerId: number; abandon: () => void } | null = null;

    const stopTimer = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const local = (e: PointerEvent) => {
      const box = layer.getBoundingClientRect();
      return { x: e.clientX - box.left, y: e.clientY - box.top };
    };
    const zoneOf = (e: PointerEvent, card = latest.current.rect): Corner | null => {
      if (!card) return null;
      // The grip moves the card: over it, the top-left corner never wakes (FR2).
      if (e.target instanceof Element && e.target.closest('[data-card-grip]')) return null;
      return cornerAt(local(e), card);
    };

    const onMove = (e: PointerEvent) => {
      if (drag) return;
      // A button held down is the plan's own gesture (a pan) or the grip's move passing through a
      // corner: a rest only counts with no button down, so neither ever wakes a corner (FR4, FR22).
      const next = e.buttons ? null : zoneOf(e);
      if (next === zone) return;
      zone = next;
      stopTimer();
      // Leaving the live corner (or moving to another) un-lives it (FR6).
      if (latest.current.live !== null && latest.current.live !== next) setLive(null);
      if (next !== null && latest.current.live !== next)
        timer = window.setTimeout(() => {
          timer = null;
          setLive(next);
        }, LIVE_DELAY_MS);
    };

    const onLeave = () => {
      if (drag) return;
      zone = null;
      stopTimer();
      setLive(null);
    };

    const onDown = (e: PointerEvent) => {
      // One resize at a time: a second pointer's press is consumed — not a pan, a grip move or a
      // second resize while the first one runs — and the same pointer pressing again (its release
      // was lost) abandons the old resize before anything else.
      if (drag) {
        if (e.pointerId !== drag.pointerId) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        drag.abandon();
      }
      const corner = latest.current.live;
      const start = latest.current.rect;
      // Not live: a press cancels the pause and stays the plan's (FR4, FR5).
      if (!corner || !start || e.button !== 0 || zoneOf(e) !== corner) {
        stopTimer();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const from = { x: e.clientX, y: e.clientY };
      let last = start;
      // The pointer can leave the card mid-drag; the resize cursor goes with it, over everything.
      const releaseCursor = holdResizeCursor(cursorFor(corner));
      setPreview({ rect: start, start, corner });

      const teardown = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        releaseCursor();
        drag = null;
        setPreview(null);
      };
      const end = (ev: PointerEvent, commit: boolean) => {
        teardown();
        const changed =
          last.left !== start.left ||
          last.top !== start.top ||
          last.w !== start.w ||
          last.h !== start.h;
        if (commit && changed) latest.current.onCommit(last);
        // The corner stays live only while the pointer is still on it (FR17); released anywhere
        // else — past a limit, over the picture — it goes quiet, and a later return rests again.
        const still =
          ev.target instanceof Node && slot.contains(ev.target) && zoneOf(ev, last) === corner;
        zone = still ? corner : null;
        if (!still) setLive(null);
        // The press was a resize, never a click on whatever is under the corner — the plan's own
        // Plan button sits in the bottom-left zone (FR3, FR10).
        if (commit) {
          const swallow = (c: MouseEvent) => {
            c.preventDefault();
            c.stopPropagation();
          };
          window.addEventListener('click', swallow, { capture: true, once: true });
          window.setTimeout(() => window.removeEventListener('click', swallow, true), 0);
        }
      };
      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        // The button came up where no pointerup reached the page (another window, say): the
        // resize was never released here, so it is cancelled, not committed (FR16).
        if (ev.pointerType === 'mouse' && ev.buttons === 0) {
          end(ev, false);
          return;
        }
        const { limits: lim, layerBox: box, inset: ins } = latest.current;
        last = resizeRect(start, corner, ev.clientX - from.x, ev.clientY - from.y, lim, box, ins);
        setPreview({ rect: last, start, corner });
      };
      const up = (ev: PointerEvent) => {
        if (ev.pointerId === e.pointerId) end(ev, true);
      };
      // A cancelled pointer puts the card back as it was and stores nothing (FR16).
      const cancel = (ev: PointerEvent) => {
        if (ev.pointerId === e.pointerId) end(ev, false);
      };
      drag = { pointerId: e.pointerId, abandon: teardown };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
    };

    slot.addEventListener('pointermove', onMove);
    slot.addEventListener('pointerleave', onLeave);
    slot.addEventListener('pointerdown', onDown, true);
    return () => {
      stopTimer();
      // Unmounted or turned off mid-resize: end it as a cancel — the page cursor back, nothing
      // stored, no click swallowed, no listener left behind.
      drag?.abandon();
      slot.removeEventListener('pointermove', onMove);
      slot.removeEventListener('pointerleave', onLeave);
      slot.removeEventListener('pointerdown', onDown, true);
    };
  }, [enabled, slotRef, layer]);

  // Turned off (the plan left the camera view, say): nothing stays live.
  useEffect(() => {
    if (!enabled) setLive(null);
  }, [enabled]);

  return { live, preview };
};
